const BASE_URL = process.env.BUN_PUBLIC_API_BASE_URL;

export interface BadgeDto {
  badge_id: string;
  name: string;
  img_url: string;
  is_visible: boolean;
  available_from: string | null;
  available_to: string | null;
  action_type: string;
  action_threshold: number;
  action_description: string;
}

export interface CreateBadgePayload {
  name: string;
  img_content?: string;
  is_visible?: boolean;
  available_from?: string | null;
  available_to?: string | null;
  action_type: string;
  action_threshold: number;
  action_description: string;
}

export interface CreateBadgeResult {
  badge_id: string;
  img_url?: string;
}

export interface UpdateBadgePayload {
  name: string;
  is_visible?: boolean;
  available_from?: string | null;
  available_to?: string | null;
  action_type: string;
  action_threshold: number;
  action_description: string;
}

export interface UserBadgeGrant {
  badge_id: string;
  name: string;
  img_url: string;
  variant_id: string;
  variant_name: string;
  filter_img_url: string | null;
  granted_at: string;
}

export interface BadgeVariantType {
  name: string;
  filter_img_url: string | null;
}

export interface BadgeVariant {
  variant_id: string;
  badge_id: string;
  variant_name: string;
  variant_description: string | null;
}

export interface AddBadgeVariantPayload {
  variant_name: string;
  variant_description?: string;
}

class BadgesProvider {
  private async request<T>(path: string, options?: RequestInit): Promise<T> {
    const res = await fetch(`${BASE_URL}${path}`, {
      ...options,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...options?.headers,
      },
    });

    if (res.status === 401) {
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
      throw new Error("Unauthorized");
    }

    if (!res.ok) {
      const text = await res.text().catch(() => "Unknown error");
      throw new Error(text || `HTTP ${res.status}`);
    }

    const text = await res.text();
    try {
      return JSON.parse(text) as T;
    } catch {
      return text as unknown as T;
    }
  }

  listAll(): Promise<BadgeDto[]> {
    return this.request<BadgeDto[]>("/v1/admin/badges");
  }

  create(payload: CreateBadgePayload): Promise<CreateBadgeResult> {
    return this.request<CreateBadgeResult>("/v1/admin/badges", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  update(badgeId: string, payload: UpdateBadgePayload): Promise<void> {
    return this.request<void>(`/v1/admin/badges/${encodeURIComponent(badgeId)}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  }

  uploadImage(badgeId: string, base64Content: string): Promise<void> {
    return this.request<void>(`/v1/admin/badges/${encodeURIComponent(badgeId)}/image`, {
      method: "POST",
      body: JSON.stringify({ content: base64Content }),
    });
  }

  delete(badgeId: string): Promise<void> {
    return this.request<void>(`/v1/admin/badges/${encodeURIComponent(badgeId)}`, {
      method: "DELETE",
    });
  }

  getForUser(userId: string): Promise<UserBadgeGrant[]> {
    return this.request<UserBadgeGrant[]>(`/v1/users/${encodeURIComponent(userId)}/badges`);
  }

  listVariantTypes(): Promise<BadgeVariantType[]> {
    return this.request<BadgeVariantType[]>("/v1/admin/badges/variant-types");
  }

  createVariantType(name: string, filterContent?: string): Promise<void> {
    return this.request<void>("/v1/admin/badges/variant-types", {
      method: "POST",
      body: JSON.stringify({ name, filter_content: filterContent }),
    });
  }

  uploadVariantTypeImage(variantName: string, base64Content: string): Promise<void> {
    return this.request<void>(
      `/v1/admin/badges/variant-types/${encodeURIComponent(variantName)}/image`,
      {
        method: "POST",
        body: JSON.stringify({ content: base64Content }),
      }
    );
  }

  removeVariantTypeImage(variantName: string): Promise<void> {
    return this.request<void>(
      `/v1/admin/badges/variant-types/${encodeURIComponent(variantName)}/image`,
      { method: "DELETE" }
    );
  }

  updateBadgeVariant(
    badgeId: string,
    variantId: string,
    variantDescription: string | null
  ): Promise<void> {
    return this.request<void>(
      `/v1/admin/badges/${encodeURIComponent(badgeId)}/variants/${encodeURIComponent(variantId)}`,
      {
        method: "PATCH",
        body: JSON.stringify({ variant_description: variantDescription }),
      }
    );
  }

  getVariantsForBadge(badgeId: string): Promise<BadgeVariant[]> {
    return this.request<BadgeVariant[]>(
      `/v1/admin/badges/${encodeURIComponent(badgeId)}/variants`
    );
  }

  addVariantToBadge(
    badgeId: string,
    payload: AddBadgeVariantPayload
  ): Promise<{ variant_id: string }> {
    return this.request<{ variant_id: string }>(
      `/v1/admin/badges/${encodeURIComponent(badgeId)}/variants`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      }
    );
  }

  deleteVariantFromBadge(badgeId: string, variantId: string): Promise<void> {
    return this.request<void>(
      `/v1/admin/badges/${encodeURIComponent(badgeId)}/variants/${encodeURIComponent(variantId)}`,
      { method: "DELETE" }
    );
  }

  grantToUser(badgeId: string, userId: string, variantName?: string): Promise<void> {
    const params = variantName ? `?variant_name=${encodeURIComponent(variantName)}` : "";
    return this.request<void>(
      `/v1/admin/badges/${encodeURIComponent(badgeId)}/grant/${encodeURIComponent(userId)}${params}`,
      { method: "POST" }
    );
  }

  revokeFromUser(badgeId: string, userId: string, variantName?: string): Promise<void> {
    const params = variantName ? `?variant_name=${encodeURIComponent(variantName)}` : "";
    return this.request<void>(
      `/v1/admin/badges/${encodeURIComponent(badgeId)}/revoke/${encodeURIComponent(userId)}${params}`,
      { method: "DELETE" }
    );
  }
}

export const badgesProvider = new BadgesProvider();
